/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable no-void */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { difference, get, omit, set, toPath } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useObservableState } from 'observable-hooks';
import Button, { ButtonContainer } from '../form/Button';
import type { ConfigHelper } from '../pages/measuringPoint/configHelper';
import type { SwarmFormController } from '../pages/measuringPoint/form-controller';
import { ErrorToNotificationsLink } from './ErrorToNotificationsLink';
import { extractEnabledFields, FormContext, OVERRIDES_NAME, useWatchNestedField } from './utils';
import { SaveLoadingButton } from './SaveLoadingButton';

interface FormProps {
    children: ReactNode;
    formController: SwarmFormController;
    configHelper?: ConfigHelper;
    DataLink?: FC<any>;
    InjectableComponent?: FC<{ showSaveButton: boolean }>;
    showSaveButton?: boolean;
    additionalSaveAction?: ReactNode;
    saveButtonLoadingKey?: string;
}

function Form({
    formController,
    InjectableComponent,
    DataLink,
    showSaveButton = true,
    additionalSaveAction,
    saveButtonLoadingKey,
    children,
}: FormProps) {
    const reactHookForm = useForm({
        formControl: formController.rhfProps.formControl,
    });

    const { setValue, getValues, watch, trigger } = formController.rhfProps;

    const overriddenFields = useWatchNestedField(watch, OVERRIDES_NAME, {});

    const parentValues = useObservableState(formController.parentValues$, null);

    const [loading, setLoading] = useState(false);

    const dataLinkProps = useMemo(
        () => ({
            setLoading,
            formController,
        }),
        [setLoading, formController]
    );

    useEffect(() => {
        function setNestedValue(name: string, sourceValues: unknown) {
            const rootName = toPath(name)[0];
            const isNested = name !== rootName;
            const value = get(sourceValues, name);

            // If this field is a nested field, merge the value under the nested path
            // onto the existing values and update the root field for the nested path
            // with the merged/updated root value. This is necessary because React Hook Form's
            // `setValue` does not support setting nested fields for fields that aren't
            // registered as nested fields. An example of such fields is
            // `notificationConfig.emailDelay`, which is part of a field originally
            // registered as `notificationConfig`.
            if (isNested) {
                const newValues = set(getValues(), name, value);
                const newRootValue = get(newValues, rootName);
                setValue(rootName, newRootValue);
            } else {
                // Set the field value to the project field value.
                setValue(name, value);
            }
        }

        // These are the fields we do NOT want to grab from the project.
        const removeParentFields = [
            ...extractEnabledFields(overriddenFields),
            ...formController.getNotInheritedFields(),
        ];

        // This is the remaining project data that needs to be loaded into the form.
        const newParentValues = omit(parentValues, removeParentFields);

        const newParentFields = extractEnabledFields(newParentValues);

        // `overriddenValuesBackup` contains a backup of the field values from before the
        // project values were merged over them. If a field is inside the
        // `overriddenValuesBackup` but no longer in the `newParentValues`, it means that
        // someone either:
        // a) Clicked the `override` checkbox for that field.
        // b) Detached the measuring point from a project (set project to null).
        // In both cases, we need to revert the form value to its state before the project
        // values were merged back into the form.
        const toBeReverted = difference(
            extractEnabledFields(formController.overriddenValuesBackup),
            newParentFields
        );

        // Revert field values for fields that were overridden.
        toBeReverted.forEach((name) => {
            setNestedValue(
                name,
                Object.fromEntries(formController.overriddenValuesBackup.entries())
            );

            formController.overriddenValuesBackup.delete(name);
        });

        // Here we are going to override the form with the new project values.
        newParentFields.forEach((name) => {
            // Store a backup of the 'before project' field value.
            if (!formController.overriddenValuesBackup.has(name)) {
                formController.overriddenValuesBackup.set(name, getValues(name));
            }

            setNestedValue(name, parentValues);
        });

        // Trigger validation.
        void trigger();
    }, [parentValues, setValue, formController, overriddenFields, getValues, trigger]);

    const context = useMemo(() => ({ formController }), [formController]);

    return (
        <FormProvider {...reactHookForm}>
            <FormContext.Provider value={context}>
                <ErrorToNotificationsLink configHelper={formController.configHelper} />
                {DataLink && <DataLink {...dataLinkProps} />}
                {loading && (
                    <div className="main-container-new">
                        <p>{gettext('LOADING')}</p>
                    </div>
                )}
                {!loading && (
                    <form
                        onSubmit={(event) => {
                            void formController.triggerSubmit(event, {
                                saveButtonKey: saveButtonLoadingKey ?? null,
                            });
                        }}
                    >
                        {children}

                        {showSaveButton && (
                            <div className="even:bg-neutral-200">
                                <div className="main-container-new py-8">
                                    <ButtonContainer>
                                        <SaveLoadingButton
                                            formController={formController}
                                            loadingKey={saveButtonLoadingKey}
                                        />
                                        {additionalSaveAction}
                                        <Button variant="secondary" href="/overview/">
                                            {gettext('CANCEL')}
                                        </Button>
                                    </ButtonContainer>
                                </div>
                            </div>
                        )}
                    </form>
                )}
                {InjectableComponent && <InjectableComponent showSaveButton={showSaveButton} />}
            </FormContext.Provider>
        </FormProvider>
    );
}

export default Form;
